import React, { useEffect } from 'react';

const SingleResponsibilityPrinciple = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Single Responsibility Principle (SRP) - SOLID Principles Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Single Responsibility Principle - the foundation of clean code. Learn why a class should have only one reason to change with real-world examples and best practices.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Single Responsibility Principle - the foundation of clean code. Learn why a class should have only one reason to change with real-world examples and best practices.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'single responsibility principle, SRP, SOLID principles, clean code, software design, object oriented programming, maintainable code');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'single responsibility principle, SRP, SOLID principles, clean code, software design, object oriented programming, maintainable code';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Single Responsibility Principle (SRP)</h1>
        <p style={styles.subtitle}>
          The First SOLID Principle - A class should have only one reason to change
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Definition</h2>
          <blockquote style={styles.blockquote}>
            "A class should have only one reason to change."
          </blockquote>
          <p style={styles.paragraph}>
            The Single Responsibility Principle states that every class should have responsibility over a single part of the functionality provided by the software, and that responsibility should be entirely encapsulated by the class. This principle is about actors and high-level architecture.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Key Concepts</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Single Reason to Change:</strong> A class should only change if the specification of one of its responsibilities changes</li>
            <li style={styles.listItem}><strong>Cohesion:</strong> Elements within a class should work together to support a single, well-defined task</li>
            <li style={styles.listItem}><strong>Separation of Concerns:</strong> Different concerns should be separated into different classes</li>
            <li style={styles.listItem}><strong>Actor:</strong> A group of users or stakeholders who would request changes to a class</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Why is SRP Important?</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Maintainability:</strong> Changes to one responsibility won't affect others</li>
            <li style={styles.listItem}><strong>Testability:</strong> Smaller, focused classes are easier to test</li>
            <li style={styles.listItem}><strong>Reusability:</strong> Single-purpose classes can be reused in different contexts</li>
            <li style={styles.listItem}><strong>Reduced Coupling:</strong> Classes become less dependent on each other</li>
            <li style={styles.listItem}><strong>Better Organization:</strong> Code becomes more organized and easier to understand</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Violation Example</h2>
          <div style={styles.codeBlock}>
{`// BAD: Violates SRP - Employee class has multiple responsibilities
class Employee {
    private String name;
    private String position;
    private double salary;
    
    // Responsibility 1: Employee data management
    public String getName() { return name; }
    public void setName(String name) { this.name = name; }
    
    // Responsibility 2: Salary calculation
    public double calculatePay() {
        // Complex salary calculation logic
        return salary * 1.1; // with bonus
    }
    
    // Responsibility 3: Database operations
    public void save() {
        // Database save logic
        Database.save(this);
    }
    
    // Responsibility 4: Report generation
    public String generateReport() {
        return "Employee: " + name + ", Salary: " + calculatePay();
    }
    
    // Responsibility 5: Tax calculation
    public double calculateTax() {
        return calculatePay() * 0.2;
    }
}`}
          </div>
          <p style={styles.paragraph}><strong>Problems with this approach:</strong></p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Changes in tax calculation affect the Employee class</li>
            <li style={styles.listItem}>Database schema changes require Employee class modification</li>
            <li style={styles.listItem}>Report format changes impact the Employee class</li>
            <li style={styles.listItem}>Difficult to test individual responsibilities</li>
            <li style={styles.listItem}>High coupling between different concerns</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Correct Implementation</h2>
          <div style={styles.codeBlock}>
{`// GOOD: Following SRP - Each class has a single responsibility

// Responsibility 1: Employee data management
class Employee {
    private String name;
    private String position;
    private double salary;
    
    public Employee(String name, String position, double salary) {
        this.name = name;
        this.position = position;
        this.salary = salary;
    }
    
    // Only getters and setters - pure data management
    public String getName() { return name; }
    public void setName(String name) { this.name = name; }
    public String getPosition() { return position; }
    public void setPosition(String position) { this.position = position; }
    public double getSalary() { return salary; }
    public void setSalary(double salary) { this.salary = salary; }
}

// Responsibility 2: Salary calculation
class PayrollCalculator {
    public double calculatePay(Employee employee) {
        // Complex salary calculation logic
        double baseSalary = employee.getSalary();
        double bonus = calculateBonus(employee);
        return baseSalary + bonus;
    }
    
    private double calculateBonus(Employee employee) {
        // Bonus calculation based on position, performance, etc.
        return employee.getSalary() * 0.1;
    }
}

// Responsibility 3: Database operations
class EmployeeRepository {
    public void save(Employee employee) {
        // Database save logic
        Database.save(employee);
    }
    
    public Employee findById(Long id) {
        return Database.findById(id);
    }
    
    public List<Employee> findAll() {
        return Database.findAll();
    }
}

// Responsibility 4: Report generation
class EmployeeReportGenerator {
    private PayrollCalculator payrollCalculator;
    
    public EmployeeReportGenerator(PayrollCalculator payrollCalculator) {
        this.payrollCalculator = payrollCalculator;
    }
    
    public String generateReport(Employee employee) {
        double pay = payrollCalculator.calculatePay(employee);
        return String.format("Employee: %s, Position: %s, Salary: %.2f", 
                           employee.getName(), employee.getPosition(), pay);
    }
    
    public String generateDetailedReport(Employee employee) {
        // More detailed report generation
        return "Detailed report for " + employee.getName();
    }
}

// Responsibility 5: Tax calculation
class TaxCalculator {
    private PayrollCalculator payrollCalculator;
    
    public TaxCalculator(PayrollCalculator payrollCalculator) {
        this.payrollCalculator = payrollCalculator;
    }
    
    public double calculateTax(Employee employee) {
        double pay = payrollCalculator.calculatePay(employee);
        return pay * getTaxRate(employee);
    }
    
    private double getTaxRate(Employee employee) {
        // Tax rate based on salary bracket, location, etc.
        return 0.2; // 20% tax rate
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Benefits of the Correct Implementation</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Each class has one clear purpose</li>
            <li style={styles.listItem}><strong>Easy to Maintain:</strong> Changes to tax calculation only affect TaxCalculator</li>
            <li style={styles.listItem}><strong>Easy to Test:</strong> Each class can be tested independently</li>
            <li style={styles.listItem}><strong>Reusable:</strong> PayrollCalculator can be used by different report generators</li>
            <li style={styles.listItem}><strong>Flexible:</strong> Easy to add new types of calculators or report formats</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Single Responsibility Principle is foundational to writing maintainable, testable, and flexible code. 
            By ensuring each class has only one reason to change, we create systems that are easier to understand, 
            modify, and extend. While it might seem to create more classes, the benefits in terms of maintainability 
            and code quality far outweigh the initial complexity.
          </p>
          <p style={styles.paragraph}>
            Remember: SRP is not about having only one method per class, but about having one reason to change. 
            A class can have multiple methods as long as they all serve the same responsibility and would change 
            for the same reason.
          </p>
        </section>
      </main>
    </article>
  );
};

export default SingleResponsibilityPrinciple;
